/**
 * Filter/sort/pagination state kept in the URL so views are shareable and survive refresh.
 */
import { today } from "@shared/dates.ts";
import { type DateRange, RANGE_PRESETS, type RangePreset, resolvePreset } from "@shared/periods.ts";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import type { SortState } from "../components/ui/DataTable.tsx";
import { useAuth } from "./auth.tsx";

export function useUrlState() {
  const [params, setParams] = useSearchParams();
  const get = useCallback((key: string) => params.get(key) ?? "", [params]);
  const set = useCallback(
    (
      updates: Record<string, string | number | null | undefined>,
      opts: { resetPage?: boolean } = { resetPage: true },
    ) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v === null || v === undefined || v === "") next.delete(k);
            else next.set(k, String(v));
          }
          if (opts.resetPage && !("page" in updates)) next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  return { params, get, set };
}

/** Clamp a ?dir= value from the URL to a valid direction. */
export function sortDir(value: string | null, fallback: "asc" | "desc"): "asc" | "desc" {
  return value === "asc" || value === "desc" ? value : fallback;
}

export function useSortState(defaultSort: string | null = null, defaultDir: "asc" | "desc" = "asc") {
  const { get, set } = useUrlState();
  const sort: SortState = {
    sort: get("sort") || defaultSort,
    dir: sortDir(get("dir"), defaultDir),
  };
  return {
    sort,
    onSort: (s: SortState) => set({ sort: s.sort, dir: s.dir }),
    page: Math.max(1, Number.parseInt(get("page"), 10) || 1),
    setPage: (page: number) => set({ page }, { resetPage: false }),
  };
}

export interface DateRangeState extends DateRange {
  preset: RangePreset;
  label: string;
  set: (preset: RangePreset, custom?: DateRange) => void;
}

/** A date range from the URL (?preset= or ?from=&to=), defaulting to the team preference. */
export function useDateRange(fallback?: Exclude<RangePreset, "custom">): DateRangeState {
  const { me } = useAuth();
  const { get, set } = useUrlState();
  const tz = me?.preferences?.timezone ?? "Europe/London";
  const defaultPreset = (fallback ?? me?.preferences?.defaultRange ?? "current_tax_year") as Exclude<
    RangePreset,
    "custom"
  >;
  const presetParam = get("preset") as RangePreset;
  const from = get("from");
  const to = get("to");
  return useMemo(() => {
    let preset: RangePreset = RANGE_PRESETS.some((p) => p.value === presetParam)
      ? presetParam
      : defaultPreset;
    let range: DateRange;
    if (preset === "custom" && from && to) range = { from, to };
    else {
      if (preset === "custom") preset = defaultPreset;
      range = resolvePreset(preset as Exclude<RangePreset, "custom">, today(tz));
    }
    const label = RANGE_PRESETS.find((p) => p.value === preset)?.label ?? "";
    return {
      ...range,
      preset,
      label,
      set: (p: RangePreset, custom?: DateRange) =>
        set(
          p === "custom" && custom
            ? { preset: "custom", from: custom.from, to: custom.to }
            : { preset: p, from: null, to: null },
        ),
    };
  }, [presetParam, from, to, defaultPreset, tz, set]);
}
